import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatNumber } from '@sohanscript/lib/utils/format';
import { Heading } from '@sohanscript/ui/heading';
import { Icons } from '@sohanscript/ui/icons';
import { TerminalFrame } from '@sohanscript/ui/terminal';
import { type ComponentProps, useMemo } from 'react';
import { getAppEnv } from '#/lib/utils/app-env.ts';
import { ContactForm } from '@/components/contact-form';
import { Socials } from '@/components/socials';
import { Button } from './ui/button';

type ContactProps = ComponentProps<'div'> & {
	transparent?: boolean;
	syncReady?: boolean;
};

const env = getAppEnv();

export function Contact({
	className,
	id,
	transparent = true,
	syncReady = false,
	...props
}: ContactProps) {
	const { titlePrefix, titleHighlight, description, element } = useMemo(() => {
		switch (env) {
			case 'fiverr':
				return {
					titlePrefix: m.heading_contact_fiverr_prefix(),
					titleHighlight: m.heading_contact_fiverr_highlight(),
					description: m.ct_fiverr_tagline({ count: formatNumber(63) }),
					element: (
						<div className="mt-12 space-y-10">
							<div className="mx-auto flex w-fit items-center gap-4 rounded-full border border-primary/10 bg-primary/5 px-5 py-2.5 backdrop-blur-sm">
								<div className="flex items-center gap-0.5">
									<Icons.star className="size-4 text-primary" />
									<Icons.star className="size-4 text-primary" />
									<Icons.star className="size-4 text-primary" />
									<Icons.star className="size-4 text-primary" />
									<Icons.star className="size-4 text-primary" />
								</div>
								<span className="h-4 w-px bg-primary/10" />
								<span className="font-mono text-sm tabular-nums tracking-tight">
									{formatNumber(5, {
										minimumFractionDigits: 1,
										maximumFractionDigits: 1,
									})}
								</span>
								<span className="text-muted-foreground text-xs">·</span>
								<span className="font-mono text-muted-foreground text-sm tabular-nums">
									{formatNumber(63)}
								</span>
								<span className="text-muted-foreground text-xs">
									{m.ct_clients()}
								</span>
							</div>
							<div className="grid place-content-center">
								<Button
									target="_blank"
									variant="hover"
									noRef
									size="lg"
									href="/fiverr"
								>
									{m.ct_order_now()}
									<Iconify icon="jam:fiverr" className="size-5" />
								</Button>
							</div>
						</div>
					),
				};

			case 'upwork':
				return {
					titlePrefix: m.heading_contact_upwork_prefix(),
					titleHighlight: m.heading_contact_upwork_highlight(),
					description: m.ct_upwork_tagline(),
					element: (
						<div className="mt-12 space-y-10">
							<div className="mx-auto flex w-fit items-center gap-4 rounded-full border border-primary/10 bg-primary/5 px-5 py-2.5 backdrop-blur-sm">
								<Iconify
									icon="lucide:circle-check"
									className="size-5 text-success"
								/>
								<span className="font-mono font-semibold text-sm text-success tracking-tight">
									{m.ct_top_rated()}
								</span>
								<span className="text-muted-foreground text-xs">·</span>
								<span className="font-mono text-muted-foreground text-sm tabular-nums">
									{formatNumber(2024, { useGrouping: false })}
								</span>
							</div>
							<div className="grid place-content-center">
								<Button
									target="_blank"
									variant="hover"
									noRef
									size="lg"
									href="/upwork"
								>
									{m.ct_hire_now()}
									<Iconify icon="tabler:brand-upwork" className="size-7" />
								</Button>
							</div>
						</div>
					),
				};

			default:
				return {
					titlePrefix: m.heading_contact_default_prefix(),
					titleHighlight: m.heading_contact_default_highlight(),
					description: m.ct_tagline_default(),
					element: (
						<>
							<ContactForm syncReady={syncReady} />
							<Socials className="my-5 justify-center lg:my-9" />
						</>
					),
				};
		}
	}, [syncReady]);

	return (
		<div className={cn('container isolate ', className)} {...props}>
			{!transparent && (
				<div className="bg-background absolute max-lg:hidden h-full max-w-xl mx-auto inset-0 -z-1" />
			)}
			<TerminalFrame id={id} className="relative max-h-auto ">
				<Heading
					titlePrefix={titlePrefix}
					titleHighlight={titleHighlight}
					description={description}
				/>
				{element}
			</TerminalFrame>
		</div>
	);
}
